import React, {useState, useEffect} from 'react';
import moment from 'moment';
import _ from 'lodash';
import {
  CheckIcon,
  BadgeCheckIcon,
  UserGroupIcon,
  UserAddIcon,
  ExternalLinkIcon,
  SparklesIcon,
  ClockIcon,
  ShieldCheckIcon,
  MicrophoneIcon,
  ArrowRightIcon,
  BookOpenIcon,
} from '@heroicons/react/outline';
import {LockClosedIcon} from '@heroicons/react/solid';

import Modal from '../components/ModalNew';
import Button, {ButtonSizes, ButtonTypes} from '../components/ButtonNew';
import {Badge} from '../components/Badge';
import {StyledTextInput} from '../components/Forms';
import Link from '../components/Link';
import {
  isWithinJoinTimeForSession,
  getUpcomingTimesForType,
  shouldHideCoaching,
  hideAIFeedbackFeatures,
  cx,
} from '../util';
import {InterviewTypes, getInterviewTypesForUser} from '../../../lib/constants/duet';
import {useAppContext, useAnalytics} from '../hooks';
import {InterviewTypeIcon} from './InterviewTypeIcon';
import {getBrandName} from '../components/Brand';

/**
 * TODOs:
 * - Add tracking
 */

export const Stages = {
  INTERVIEW_TYPE: 'interview_type',
  PRACTICE_TYPE: 'practice_type',
  SURVEY: 'survey',
  INVITE: 'invite',
  SCHEDULE: 'schedule',
  SUCCESS: 'success',
};

export const PracticeTypes = {
  PEER: {
    key: 'peer',
    name: 'Practice with peers',
    description: `Free mock interviews with other ${getBrandName()} users where you take turns asking questions.`,
    Icon: UserGroupIcon,
  },
  EXPERT: {
    key: 'expert',
    name: 'Expert mock interview',
    description: 'Get interviewed 1-1 by an expert coach with FAANG+ experience.',
    showExternalLink: true,
    Icon: BadgeCheckIcon,
    shouldHide: ({currentUser}) => shouldHideCoaching(currentUser),
  },
  INVITE: {
    key: 'invite',
    name: 'Practice with a friend',
    description: 'Invite a friend and practice on your own schedule at any time.',
    Icon: UserAddIcon,
    // Disable until PWAF better supports SQL
    shouldHide: (ctx, request) => request.type === InterviewTypes.SQL.key,
  },
};

const getPracticeType = (key) => _.find(Object.values(PracticeTypes), ['key', key]);

const PracticeTypesArray = [
  PracticeTypes.PEER,
  PracticeTypes.INVITE,
  PracticeTypes.EXPERT,
];

const InterviewTypeCoachingLink = {
  PM: '/coaching?role=pm&category=mock_interviews&src=practice-modal',
  Behavioral: '/coaching?category=mock_interviews&src=practice-modal',
  Algorithms: '/coaching?role=swe&category=mock_interviews&src=practice-modal',
  'System-Design': '/coaching?skill=system-design-skill&src=practice-modal',
  SQL: '/coaching?skill=sql-skill&src=practice-modal',
  'Data-Science-ML': '/coaching?role=data-science&src=practice-modal',
  Frontend: '/coaching?role=swe&category=mock_interviews&src=practice-modal',
};

const InterviewLevels = {
  BEGINNER: {
    label: 'Beginner',
    value: 1,
    description: 'I am new to peer mock interviews.',
  },
  INTERMEDIATE: {
    label: 'Intermediate',
    value: 2,
    description: 'I have done several interviews already.',
  },
  ADVANCED: {
    label: 'Advanced',
    value: 3,
    description: 'I am a Jedi Master of mock interviews.',
  },
};

const InterviewLevelsArray = [
  InterviewLevels.BEGINNER,
  InterviewLevels.INTERMEDIATE,
  InterviewLevels.ADVANCED,
];

const formatSessionDate = (sessionTime) => {
  const sessionTimeObj = moment(sessionTime);

  if (sessionTimeObj.isSame(moment(), 'day')) {
    return 'Today';
  } if (sessionTimeObj.isSame(moment().add(1, 'day'), 'day')) {
    return 'Tomorrow';
  }
  return sessionTimeObj.format('dddd, MMMM D');
};

const DuetInterviewType = ({
  draftRequest, nextMatchTimes, onSelectInterviewType, onClose,
}) => {
  const {currentUser} = useAppContext();
  // Only offer types the user could actually be matched for
  const interviewTypes = getInterviewTypesForUser(currentUser?.profile)
    .filter((t) => !t.disabled)
    .filter((t) => getUpcomingTimesForType(t.key, nextMatchTimes).length > 0);
  const [selectedType, setSelectedType] = useState(draftRequest.type || interviewTypes[0]?.key);

  return (
    <div>
      <div className="md:grid md:gap-3 md:grid-cols-2" role="radiogroup" aria-label="Interview type">
        {interviewTypes.map((interviewType) => (
          <Button
            key={interviewType.key}
            onClick={() => setSelectedType(interviewType.key)}
            buttonType={ButtonTypes.SECONDARY}
            className={cx('relative w-full h-24 mb-3 md:mb-0', {'ring-2 ring-offset-2 ring-indigo-500': selectedType === interviewType.key})}
            role="radio"
            aria-checked={selectedType === interviewType.key}
          >
            {interviewType.badge && (
              <span className="absolute -top-2 -right-2 whitespace-nowrap rounded-full bg-indigo-600 px-2 py-1 text-xs font-semibold text-white">
                {interviewType.badge}
              </span>
            )}
            <div className="w-full pl-3 flex items-center justify-start text-left gap-3">
              <InterviewTypeIcon typeKey={interviewType.key} />
              <div className="flex flex-col">
                <div className="flex items-center text-lg font-medium leading-5 mb-1">
                  {interviewType.name}
                </div>
                <div className="text-gray-500 text-sm leading-tight font-normal">
                  {interviewType.description}
                </div>
              </div>
            </div>
          </Button>
        ))}
      </div>

      <div className="flex justify-end w-full pt-4 gap-2">
        <Button
          buttonType={ButtonTypes.SECONDARY}
          size={ButtonSizes.LARGE}
          className="w-full"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          size={ButtonSizes.LARGE}
          className="w-full"
          onClick={() => onSelectInterviewType(selectedType)}
          disabled={!selectedType}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const DuetPracticeType = ({draftRequest, onSelectPracticeType, onBack}) => {
  const appContext = useAppContext();
  const [selectedType, setSelectedType] = useState(draftRequest.mode || PracticeTypes.PEER.key);

  const getButtonLink = (practiceType) => (practiceType === PracticeTypes.EXPERT.key
    ? InterviewTypeCoachingLink[draftRequest?.type]
    : null
  );

  return (
    <div>
      <div role="radiogroup" aria-label="Practice type">
        {PracticeTypesArray.map((pt) => (pt.shouldHide?.(appContext, draftRequest)
          ? null
          : (
            <Button
              key={pt.key}
              className={cx('w-full mb-3 h-24', {
                'ring-2 ring-offset-2 ring-indigo-500': selectedType === pt.key,
                'bg-gray-100 hover:bg-gray-100': pt.shouldDisable?.(appContext),
              })}
              buttonType={ButtonTypes.SECONDARY}
              onClick={() => setSelectedType(pt.key)}
              role="radio"
              aria-checked={selectedType === pt.key}
              href={getButtonLink(pt.key)}
              target="_blank"
            >
              <div className="pl-3 flex items-center w-full text-left gap-5">
                <pt.Icon className="h-7 w-7 text-gray-700 flex-shrink-0" />
                <div className="flex flex-col">
                  <div className="flex items-center text-lg font-medium">
                    {pt.name}
                    {pt.shouldDisable?.(appContext) && <LockClosedIcon className="h-4 w-4 ml-2" aria-hidden="true" />}
                    {pt.showExternalLink && <ExternalLinkIcon className="h-4 w-4 inline-block ml-2 text-gray-400" />}
                    {pt.hasBadge?.(appContext) && <Badge className="ml-2 whitespace-nowrap">{pt.hasBadge?.(appContext)}</Badge>}
                  </div>
                  <div className="text-gray-500 text-sm leading-tight font-normal">
                    {pt.description}
                  </div>
                </div>
              </div>
            </Button>
          )))}
      </div>

      <div className="flex justify-end w-full pt-4 gap-2">
        <Button
          buttonType={ButtonTypes.SECONDARY}
          size={ButtonSizes.LARGE}
          className="w-full"
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          size={ButtonSizes.LARGE}
          className="w-full"
          onClick={() => onSelectPracticeType(selectedType)}
          disabled={
            !selectedType
            || selectedType === PracticeTypes.EXPERT.key
            || getPracticeType(selectedType)?.shouldDisable?.(appContext)
          }
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const DuetOnDemandInvite = ({onInvite, onBack}) => {
  const [email, setEmail] = useState('');

  return (
    <>
      <p className="text-gray-600 text-base leading-tight font-normal mb-6">
        {'Know someone you\'d like to practice with? Send them an invite!'}
        <br />
        <br />
        Your session will begin as soon as you send out the invite, and will remain
        available for 2 hours, or until one of you decides to end the session.
      </p>
      <StyledTextInput onChange={(e) => setEmail(e.target.value)} value={email} placeholder="Email address" />

      <div className="flex justify-end w-full pt-8 gap-2">
        <Button
          buttonType={ButtonTypes.SECONDARY}
          size={ButtonSizes.LARGE}
          className="w-full"
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          size={ButtonSizes.LARGE}
          className="w-full"
          onClick={() => onInvite(email)}
        >
          Invite and start session
        </Button>
      </div>
    </>
  );
};

const DuetSurvey = ({
  draftRequest,
  onSubmitSurvey,
  onBack,
  submitLabel = 'Next',
}) => {
  const [selectedLevel, setSelectedLevel] = useState(draftRequest.data?.interview_level
    || InterviewLevels.BEGINNER.value);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onSubmitSurvey({interview_level: selectedLevel});
  };

  return (
    <>
      <div className="flex justify-center -mt-4 mb-4 font-semibold text-gray-500">
        This will be used to help match you with the best partner
      </div>
      <div role="radiogroup" aria-label="Interview level">
        {InterviewLevelsArray.map((interviewLevel) => (
          <Button
            key={interviewLevel.label}
            className={cx('w-full mb-3 h-24', {
              'ring-2 ring-offset-2 ring-indigo-500': selectedLevel === interviewLevel.value,
            })}
            buttonType={ButtonTypes.SECONDARY}
            onClick={() => setSelectedLevel(interviewLevel.value)}
            role="radio"
            aria-checked={selectedLevel === interviewLevel.value}
          >
            <div className="pl-3 flex items-center w-full text-left gap-5">
              <div className="flex flex-col">
                <div className="text-lg font-medium">
                  {interviewLevel.label}
                </div>
                <div className="text-gray-500 text-sm leading-tight font-normal">
                  {interviewLevel.description}
                </div>
              </div>
            </div>
          </Button>
        ))}
      </div>
      <div className="flex justify-end w-full pt-4 gap-2">
        <Button
          buttonType={ButtonTypes.SECONDARY}
          size={ButtonSizes.LARGE}
          className="w-full"
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          size={ButtonSizes.LARGE}
          className="w-full"
          onClick={handleSubmit}
          disabled={!selectedLevel || isSubmitting}
        >
          {submitLabel}
        </Button>
      </div>
    </>
  );
};

const DuetSchedule = ({
  type,
  nextMatchTimes,
  upcomingRequests,
  selectedSessionTime = null,
  onChangeSessionTime,
  onSelectSessionTime,
  onBack,
  isFinalStep = true,
}) => {
  const [sessionTimesByDate, setSessionTimesByDate] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const updateTimeDependentState = () => {
      const nextSessionTimes = getUpcomingTimesForType(type, nextMatchTimes);

      const byDate = _.groupBy(nextSessionTimes, formatSessionDate);
      setSessionTimesByDate(byDate);

      if (selectedSessionTime && !nextSessionTimes.includes(selectedSessionTime)) {
        onChangeSessionTime(null);
      }
    };

    updateTimeDependentState();
    const intervalId = setInterval(updateTimeDependentState, 1000);

    return () => clearInterval(intervalId);
  }, [nextMatchTimes, type, selectedSessionTime]);

  const scheduleOrJoinLabel = isWithinJoinTimeForSession(selectedSessionTime) ? 'Join' : 'Schedule';
  const submitLabel = isFinalStep ? scheduleOrJoinLabel : 'Next';

  const handleSubmit = async (sessionTime) => {
    setIsSubmitting(true);
    await onSelectSessionTime(sessionTime);
  };

  return (
    <>
      <div className="overflow-scroll px-2 -mx-2 overscroll-contain" role="radiogroup" aria-label="Session time" style={{maxHeight: '400px'}}>
        {Object.entries(sessionTimesByDate).map(([day, sessionTimes]) => (
          <div key={day} className="mb-4">
            <div className="text-base font-semibold mb-2">{day}</div>
            {sessionTimes.map((sessionTime) => {
              const isWithinJoinTime = isWithinJoinTimeForSession(sessionTime);
              const isAlreadyRequested = _.some(upcomingRequests, ['sessionTime', sessionTime]);

              return (
                <Button
                  key={sessionTime}
                  onClick={() => onChangeSessionTime(sessionTime)}
                  buttonType={ButtonTypes.SECONDARY}
                  disabled={isAlreadyRequested}
                  className={cx('relative w-full mb-3', {'ring-2 ring-offset-2 ring-indigo-500': selectedSessionTime === sessionTime})}
                  role="radio"
                  aria-checked={selectedSessionTime === sessionTime}
                >
                  {moment(sessionTime).format('h:mm A')}
                  {isWithinJoinTime && (
                    <span className="absolute right-1 px-2.5 py-0.5 rounded-full font-bold bg-indigo-100 text-indigo">
                      Starting now
                    </span>
                  )}
                </Button>
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex justify-end w-full pt-4 gap-2">
        <Button
          buttonType={ButtonTypes.SECONDARY}
          size={ButtonSizes.LARGE}
          className="w-full"
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          size={ButtonSizes.LARGE}
          className="w-full"
          onClick={() => handleSubmit(selectedSessionTime)}
          disabled={!selectedSessionTime || isSubmitting}
        >
          {submitLabel}
        </Button>
      </div>
    </>
  );
};

const DuetSchedulingModal = ({
  isOpen,
  onScheduleRequest,
  onCreateOnDemandSession,
  onClose,
  nextMatchTimes,
  upcomingRequests,
  canScheduleInterview,
  initialStage = Stages.INTERVIEW_TYPE,
  prefill = null,
}) => {
  const {currentUser, hasSubscription} = useAppContext();
  const {tracker} = useAnalytics();
  const [draftRequest, setDraftRequest] = useState({});
  const [scheduledRequest, setScheduledRequest] = useState(null);
  const [stage, setStage] = useState(initialStage);
  // Stack of visited stages, so Back returns to the previous screen regardless
  // of where the flow was entered (PeerMockCard can open mid-flow).
  const [stageHistory, setStageHistory] = useState([initialStage]);

  // The canonical step order, each paired with a predicate for whether its data
  // is already present on the draft. The modal can be opened to any step (with
  // some fields preseeded); after a step completes we walk to the first step
  // still missing its data, and when none are missing we submit.
  const CanonicalSteps = [
    {stage: Stages.INTERVIEW_TYPE, isDone: (draft) => !!draft.type, field: 'type'},
    {stage: Stages.PRACTICE_TYPE, isDone: (draft) => !!draft.mode, field: 'mode'},
    {stage: Stages.SURVEY, isDone: (draft) => !!draft.data?.interview_level, field: 'data'},
    {stage: Stages.SCHEDULE, isDone: (draft) => !!draft.sessionTime, field: 'sessionTime'},
  ];
  const firstIncompleteStep = (draft) => CanonicalSteps.find((step) => !step.isDone(draft));
  const completesDraft = (stageToCheck, draft) => CanonicalSteps
    .every((step) => step.stage === stageToCheck || step.isDone(draft));

  const goToStage = (nextStage) => {
    setStage(nextStage);
    setStageHistory((history) => [...history, nextStage]);
  };

  const submitRequest = async (draft) => {
    const request = await onScheduleRequest?.({
      type: draft.type,
      data: draft.data,
      sessionTime: draft.sessionTime,
    });
    setScheduledRequest(request);

    if (!isWithinJoinTimeForSession(draft.sessionTime)) setStage(Stages.SUCCESS);
  };

  // Merge a step's data into the draft, then either advance to the next
  // incomplete step or submit if the draft is now complete. Returns the submit
  // promise so the triggering step can keep its button disabled until it lands.
  const advance = (draft) => {
    setDraftRequest(draft);
    const next = firstIncompleteStep(draft);
    if (next) {
      goToStage(next.stage);
      return Promise.resolve();
    }
    return submitRequest(draft);
  };

  useEffect(() => {
    if (!isOpen) return;
    // Open to a specific step with some draft fields preseeded, or start the full flow
    const draft = prefill?.draft ? {...prefill.draft} : {};
    const entryStage = prefill?.stage || initialStage;
    setDraftRequest(draft);
    setStage(entryStage);
    setStageHistory([entryStage]);
  }, [isOpen, initialStage, prefill]);

  const handleSelectInterviewType = (type) => {
    advance({...draftRequest, type});
  };

  const handleSelectPracticeType = async (mode) => {
    if (mode === PracticeTypes.INVITE.key) {
      setDraftRequest((draft) => ({...draft, mode}));
      goToStage(Stages.INVITE);
    } else {
      advance({...draftRequest, mode});
    }
  };

  const handleOnDemandSession = async (invitedEmail) => (
    onCreateOnDemandSession(invitedEmail, draftRequest.type)
  );

  const handleSubmitSurvey = (data) => advance({...draftRequest, data});

  const handleChangeSessionTime = (sessionTime) => (
    setDraftRequest((draft) => ({...draft, sessionTime}))
  );

  const handleSelectSessionTime = (sessionTime) => advance({...draftRequest, sessionTime});

  const goBack = () => {
    if (stageHistory.length <= 1) {
      onClose();
      return;
    }
    // Clear the data owned by the step we're leaving
    const leavingStep = CanonicalSteps.find((step) => step.stage === stage);
    if (leavingStep) setDraftRequest((draft) => _.omit(draft, leavingStep.field));
    const previous = stageHistory.slice(0, -1);
    setStage(previous[previous.length - 1]);
    setStageHistory(previous);
  };

  const onClickSchedule = () => {
    // Reset modal to beginning of the full flow
    setDraftRequest({});
    setStage(initialStage);
    setStageHistory([initialStage]);
  };

  const hideAIFeedback = hideAIFeedbackFeatures(currentUser, scheduledRequest);
  const scheduleOrJoinLabel = isWithinJoinTimeForSession(draftRequest.sessionTime) ? 'Join' : 'Schedule';
  const surveySubmitLabel = completesDraft(Stages.SURVEY, draftRequest) ? scheduleOrJoinLabel : 'Next';

  switch (stage) {
    case (Stages.INTERVIEW_TYPE):
      return (
        <Modal
          header="Select your interview type"
          isOpen={isOpen}
          onClose={onClose}
          showCloseButton
          modalClassName="max-w-2xl"
        >
          <DuetInterviewType
            draftRequest={draftRequest}
            nextMatchTimes={nextMatchTimes}
            onSelectInterviewType={handleSelectInterviewType}
            onClose={onClose}
          />
        </Modal>
      );
    case (Stages.PRACTICE_TYPE):
      return (
        <Modal
          header="Select your practice type"
          isOpen={isOpen}
          onClose={onClose}
          showCloseButton
        >
          <DuetPracticeType
            draftRequest={draftRequest}
            onSelectPracticeType={handleSelectPracticeType}
            onBack={goBack}
          />
        </Modal>
      );
    case (Stages.INVITE):
      return (
        <Modal
          header="Invite a peer to practice"
          isOpen={isOpen}
          onClose={onClose}
          showCloseButton
        >
          <DuetOnDemandInvite
            onInvite={handleOnDemandSession}
            onBack={goBack}
          />
        </Modal>
      );
    case (Stages.SURVEY):
      return (
        <Modal
          header="Choose your interview level"
          isOpen={isOpen}
          onClose={onClose}
          showCloseButton
        >
          <DuetSurvey
            draftRequest={draftRequest}
            onSubmitSurvey={handleSubmitSurvey}
            onBack={goBack}
            submitLabel={surveySubmitLabel}
          />
        </Modal>
      );
    case (Stages.SCHEDULE):
      return (
        <Modal
          header="Select a time to practice"
          isOpen={isOpen}
          onClose={onClose}
          showCloseButton
        >
          <div className="flex justify-center -mt-4 mb-4 font-semibold text-gray-500">
            {`All times shown in your local timezone (${moment.tz(moment.tz.guess()).format('z')})`}
          </div>
          <DuetSchedule
            type={draftRequest.type}
            nextMatchTimes={nextMatchTimes}
            upcomingRequests={upcomingRequests}
            selectedSessionTime={draftRequest.sessionTime}
            onChangeSessionTime={handleChangeSessionTime}
            onSelectSessionTime={handleSelectSessionTime}
            onBack={goBack}
            isFinalStep={completesDraft(Stages.SCHEDULE, draftRequest)}
          />
        </Modal>
      );
    case (Stages.SUCCESS):
      return (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          showCloseButton
        >
          <div className="mt-4 mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
          </div>

          <div className="my-4 text-center text-lg  font-semibold text-gray-900">
            Your interview is confirmed!
          </div>

          <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 mb-3">
            <ClockIcon className="h-7 w-7 mr-3 shrink-0" />
            <div>
              {'Your '}
              <span className="font-semibold">{InterviewTypes[scheduledRequest.type].name}</span>
              {' interview is '}
              <span className="font-semibold">{moment(scheduledRequest.sessionTime).format('dddd, MMMM D, [at] h:mm A')}</span>
              {'. '}
              <Link
                href={scheduledRequest.googleCalendarUrl}
                target="_blank"
                className="text-indigo font-semibold"
              >
                Add to your calendar
              </Link>
              {' so you don\'t forget.'}
            </div>
          </div>

          {scheduledRequest?.question && (
            <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 mb-3">
              <BookOpenIcon className="h-7 w-7 mr-3 shrink-0" />
              <div>
                {'Your assigned question to ask is '}
                <Link
                  href={`/practice/prepare/${scheduledRequest.question.slug}`}
                  className="text-indigo font-semibold"
                >
                  {`${scheduledRequest.question.title}.`}
                </Link>
                {' Take a moment to review the question beforehand.'}
              </div>
            </div>
          )}

          <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 mb-3">
            <ShieldCheckIcon className="h-7 w-7 mr-3 shrink-0" />
            <div>
              {'Review our '}
              <Link
                href="/guidelines"
                className="text-indigo font-semibold"
                onClick={() => tracker.duetClickGuidelines()}
                target="_blank"
              >
                community guidelines
              </Link>
              {' and be respectful to your partner. Please arrive on time and take turns.'}
            </div>
          </div>

          <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 mb-3">
            <MicrophoneIcon className="h-7 w-7 mr-3 shrink-0" />
            <div>
              {/* TODO */}
              Make sure you have a working camera and microphone.
            </div>
          </div>

          {!hideAIFeedback && hasSubscription && (
            <Link
              href="/account#aced-practice"
              className="flex items-center bg-indigo-100 border border-indigo rounded-lg px-4 py-3 mb-3"
            >
              <SparklesIcon className="h-7 w-7 mr-3 shrink-0 text-indigo" />
              <div>
                <span className="text-indigo font-semibold">
                  AI feedback.
                </span>
                {` ${getBrandName()} can automatically generate AI transcriptions and feedback for you. `}
                <span className="text-indigo font-semibold">
                  Manage settings
                </span>
                <ArrowRightIcon className="h-4 ml-1 text-indigo" />
              </div>
            </Link>
          )}

          {!hideAIFeedback && !hasSubscription && (
            <>
              <Link
                href="/upgrade?src=practice-schedule-modal-ai"
                className="bg-indigo-100 border border-indigo rounded-lg px-4 py-3 mb-3 flex items-center"
              >
                <SparklesIcon className="h-7 w-7 mr-3 shrink-0 text-indigo" />
                <div>
                  <span className="text-indigo font-semibold">
                    Get AI feedback.
                  </span>
                  {` ${getBrandName()} can automatically generate AI transcriptions and feedback for you. `}
                  <span className="text-indigo font-semibold">
                    Unlock now
                  </span>
                  <ArrowRightIcon className="h-4 ml-1 text-indigo" />
                </div>
              </Link>
              <Link
                href="/account#aced-practice"
                className="block -mt-2 underline"
              >
                Manage settings
              </Link>
            </>
          )}

          <div className="flex justify-end w-full pt-6 gap-2">
            <Button
              href={scheduledRequest.googleCalendarUrl}
              target="_blank"
              buttonType={ButtonTypes.SECONDARY}
              size={ButtonSizes.LARGE}
              className="w-full"
            >
              Add to Calendar
            </Button>
            <Button
              size={ButtonSizes.LARGE}
              className="w-full"
              href={canScheduleInterview ? null : '/upgrade?src=practice-modal'}
              onClick={canScheduleInterview ? onClickSchedule : null}
            >
              {!canScheduleInterview && <LockClosedIcon className="h-4 w-4 mr-2 text-white" aria-hidden="true" />}
              Schedule another
            </Button>
          </div>
        </Modal>
      );
    default:
      throw new Error(`Invalid stage for DuetSchedulingModal: ${stage}`);
  }
};

export default DuetSchedulingModal;
